import { MediaPicker } from '@/components/media/MediaPicker';
import type { MediaRow } from '@/routes/media/types';
import type { Locale } from '@sjuni/types';
import { Avatar, Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { thumbUrl } from '@/lib/media/thumbUrl';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslatableListField } from '@/components/form/TranslatableListField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { listToInput, valueFor } from '@/lib/forms/translatable';
import { useResourceForm } from '@/lib/forms/useResourceForm';
import { useListParams } from '@/lib/list/useListParams';
import type { DepartmentRow, FacultyRow } from '@/routes/academic/types';

import { StaffImportWizard } from './StaffImportWizard';
import { STAFF_RANKS, type StaffInput, type StaffRank, type StaffRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.5 — `/staff`.
 *
 * The importer is a route of its own rather than a dialog: it has three steps,
 * one of which is a table of every row in the file, and none of that fits in a
 * modal on the laptop screens this is used on.
 *
 * §5.5's fourth bullet — "Photo upload with a square crop tool" — waits on the
 * media library (docs/09-BUILD-PLAN.md step 4, still open on the schema
 * question in docs/03-DATA-MODEL.md). Until then the field takes the path a
 * migrated record already carries, so nothing is lost in the meantime.
 */
export function StaffPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  if (rest === 'import') {
    return <StaffImportWizard />;
  }

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can } = useAuth();
  const list = useListParams('sort_order');

  const faculties = useResourceList<FacultyRow>('faculties', { per_page: 100, sort: 'sort_order' });

  const columns: Column<StaffRow>[] = [
    {
      key: 'name',
      label: t('staff.columns.name'),
      render: (row) => {
        const name =
          `${valueFor(row.last_name, locale)} ${valueFor(row.first_name, locale)}`.trim();

        return (
          <div className="flex min-w-52 items-center gap-3">
            <Avatar
              src={thumbUrl(row.photo)}
              alt={name}
              initials={initialsOf(row, locale)}
              size="sm"
            />

            <div>
              <span className="font-semibold text-primary">{name}</span>
              <span className="mt-0.5 block text-caption text-muted">
                {valueFor(row.position, locale) || '—'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'rank',
      label: t('staff.columns.rank'),
      render: (row) => (
        <span className="text-caption text-secondary">{t(`staff.ranks.${row.rank}`)}</span>
      ),
    },
    {
      key: 'faculty',
      label: t('academic.departments.columns.faculty'),
      // Clamped and stacked. A faculty here is "განათლების, ჰუმანიტარულ და
      // სოციალურ მეცნიერებათა ფაკულტეტი" — set on one line with its department
      // it makes a row five lines tall and the table unreadable.
      render: (row) => (
        <div className="max-w-56">
          <span className="line-clamp-2 text-caption text-secondary">
            {row.faculty ? valueFor(row.faculty.name, locale) : '—'}
          </span>

          {row.department ? (
            <span className="line-clamp-1 text-caption text-muted">
              {valueFor(row.department.name, locale)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'contact',
      label: t('staff.columns.contact'),
      render: (row) => (
        <span className="text-caption text-secondary">{row.email ?? row.phone ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
  ];

  return (
    <ResourceListScreen
      resource="staff"
      title={t('nav.items.staff')}
      intro={t('staff.intro')}
      columns={columns}
      labelFor={(row) => `${valueFor(row.last_name, locale)} ${valueFor(row.first_name, locale)}`}
      defaultSort="sort_order"
      emptyTitle={t('staff.empty.title')}
      emptyBody={t('staff.empty.body')}
      newLabel={t('staff.new')}
      deleteTitle={t('staff.delete.title')}
      deleteBody={(label) => t('staff.delete.body', { label })}
      secondaryAction={
        // Gated on create, which is what the endpoint asks for.
        can('staff.create') ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            iconLeft={<Upload size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => navigate('/staff/import')}
          >
            {t('staff.import.action')}
          </Button>
        ) : null
      }
      filters={
        <>
          <Select
            label={t('academic.departments.columns.faculty')}
            hideLabel
            value={String(list.params.faculty_id ?? '')}
            onChange={(event) => list.setFilter('faculty_id', event.target.value || null)}
            options={[
              { value: '', label: t('academic.anyFaculty') },
              ...(faculties.data?.data ?? []).map((faculty) => ({
                value: String(faculty.id),
                label: valueFor(faculty.name, locale),
              })),
            ]}
          />

          <Select
            label={t('staff.columns.rank')}
            hideLabel
            value={String(list.params.rank ?? '')}
            onChange={(event) => list.setFilter('rank', event.target.value || null)}
            options={[
              { value: '', label: t('staff.anyRank') },
              ...STAFF_RANKS.map((rank) => ({ value: rank, label: t(`staff.ranks.${rank}`) })),
            ]}
          />
        </>
      }
    />
  );
}

interface Fields {
  first_name: TranslatedValue;
  last_name: TranslatedValue;
  slug: TranslatedValue;
  position: TranslatedValue;
  rank: StaffRank;
  faculty_id: string;
  department_id: string;
  email: string;
  phone: string;
  photo_media_id: number | null;
  photo: MediaRow | null;
  bio: TranslatedValue;
  education: TranslatedValue;
  publications: TranslatedValue;
  research_interests: Record<string, string[] | null>;
  courses: Record<string, string[] | null>;
  orcid: string;
  scholar_url: string;
  researchgate_url: string;
  linkedin_url: string;
  is_academic: boolean;
  sort_order: number;
  status: StaffRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can, hasRole, user } = useAuth();

  const mayPublish = can('staff.publish');
  const scoped = hasRole('faculty-admin');

  const faculties = useResourceList<FacultyRow>('faculties', { per_page: 100, sort: 'sort_order' });

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<StaffRow, Fields, StaffInput>({
      resource: 'staff',
      recordId,
      empty: () => empty(scoped ? String(user?.faculty?.id ?? '') : ''),
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'first_name',
        'last_name',
        'slug',
        'position',
        'rank',
        'faculty_id',
        'department_id',
        'email',
        'phone',
        'photo_media_id',
        'orcid',
        'scholar_url',
        'researchgate_url',
        'linkedin_url',
        'sort_order',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  // Only the chosen faculty's departments — the server refuses one from
  // another faculty, so the selector never offers it.
  const departments = useResourceList<DepartmentRow>('departments', {
    per_page: 100,
    sort: 'sort_order',
    ...(values.faculty_id === '' ? {} : { faculty_id: Number(values.faculty_id) }),
  });

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('staff.new') : t('staff.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/staff')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <TranslatableField
              label={t('staff.fields.firstName')}
              name="first_name"
              value={values.first_name}
              onChange={(next) => setValue('first_name', next, { shouldDirty: true })}
              error={formState.errors.first_name?.message}
            />

            <TranslatableField
              label={t('staff.fields.lastName')}
              name="last_name"
              value={values.last_name}
              onChange={(next) => setValue('last_name', next, { shouldDirty: true })}
              error={formState.errors.last_name?.message}
            />
          </div>

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record?.slug}
            published={values.status === 'published'}
            error={formState.errors.slug?.message}
          />

          {/* Not optional in §4: the directory prints it under every name, and
              a card with a name and nothing else says nothing. */}
          <TranslatableField
            label={t('staff.fields.position')}
            hint={t('staff.fields.positionHint')}
            name="position"
            value={values.position}
            onChange={(next) => setValue('position', next, { shouldDirty: true })}
            error={formState.errors.position?.message}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('staff.columns.rank')}
              {...register('rank')}
              error={formState.errors.rank?.message}
              options={STAFF_RANKS.map((rank) => ({
                value: rank,
                label: t(`staff.ranks.${rank}`),
              }))}
            />

            <Select
              label={t('academic.departments.columns.faculty')}
              hint={scoped ? t('academic.lockedToYourFaculty') : undefined}
              {...register('faculty_id')}
              disabled={scoped}
              error={formState.errors.faculty_id?.message}
              options={[
                { value: '', label: t('staff.noFaculty') },
                ...(faculties.data?.data ?? []).map((faculty) => ({
                  value: String(faculty.id),
                  label: valueFor(faculty.name, locale),
                })),
              ]}
            />

            <Select
              label={t('academic.programs.fields.department')}
              {...register('department_id')}
              error={formState.errors.department_id?.message}
              options={[
                { value: '', label: t('academic.programs.noDepartment') },
                ...(departments.data?.data ?? []).map((department) => ({
                  value: String(department.id),
                  label: valueFor(department.name, locale),
                })),
              ]}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('content.fields.order')}
              hint={t('content.fields.orderHint')}
            />
          </div>

          <fieldset className="grid gap-4 rounded-card border border-subtle p-4 sm:grid-cols-2">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('staff.contact')}
            </legend>

            <Input
              {...register('email')}
              type="email"
              label={t('staff.fields.email')}
              error={formState.errors.email?.message}
            />

            <Input
              {...register('phone')}
              type="tel"
              label={t('staff.fields.phone')}
              error={formState.errors.phone?.message}
            />

            {/*
              A path, until the media library lands (step 4). Typed rather than
              picked, so a migrated record keeps its photo and nothing has to be
              re-uploaded later.
            */}
            <MediaPicker
              label={t('staff.fields.photo')}
              hint={t('staff.fields.photoHint')}
              value={values.photo}
              onChange={(mediaId, media) => {
                setValue('photo_media_id', mediaId, { shouldDirty: true });
                setValue('photo', media, { shouldDirty: true });
              }}
              error={formState.errors.photo_media_id?.message}
            />
          </fieldset>

          <RichTextField
            label={t('staff.fields.bio')}
            value={values.bio}
            onChange={(next) => setValue('bio', next, { shouldDirty: true })}
          />

          <RichTextField
            label={t('staff.fields.education')}
            value={values.education}
            onChange={(next) => setValue('education', next, { shouldDirty: true })}
          />

          <RichTextField
            label={t('staff.fields.publications')}
            value={values.publications}
            onChange={(next) => setValue('publications', next, { shouldDirty: true })}
          />

          {/* Lists, not rich text — the public profile draws both as `<ul>`s. */}
          <TranslatableListField
            label={t('staff.fields.research')}
            hint={t('staff.fields.researchHint')}
            value={values.research_interests}
            onChange={(next) => setValue('research_interests', next, { shouldDirty: true })}
          />

          <TranslatableListField
            label={t('staff.fields.courses')}
            hint={t('staff.fields.coursesHint')}
            value={values.courses}
            onChange={(next) => setValue('courses', next, { shouldDirty: true })}
          />

          <fieldset className="grid gap-4 rounded-card border border-subtle p-4 sm:grid-cols-2">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('staff.academicProfiles')}
            </legend>

            <Input
              {...register('orcid')}
              label={t('staff.fields.orcid')}
              hint={t('staff.fields.orcidHint')}
              error={formState.errors.orcid?.message}
            />

            <Input
              {...register('scholar_url')}
              type="url"
              label={t('staff.fields.scholar')}
              error={formState.errors.scholar_url?.message}
            />

            <Input
              {...register('researchgate_url')}
              type="url"
              label={t('staff.fields.researchgate')}
              error={formState.errors.researchgate_url?.message}
            />

            <Input
              {...register('linkedin_url')}
              type="url"
              label={t('staff.fields.linkedin')}
              error={formState.errors.linkedin_url?.message}
            />
          </fieldset>

          <label className="flex items-center gap-2 text-body-sm text-primary">
            <input
              type="checkbox"
              {...register('is_academic')}
              className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            />
            {t('staff.fields.isAcademic')}
          </label>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="staff"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              last_name: { label: t('staff.fields.lastName'), value: values.last_name },
              position: { label: t('staff.fields.position'), value: values.position },
              bio: { label: t('staff.fields.bio'), value: values.bio },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

/**
 * The two letters the avatar falls back to when there is no photo.
 *
 * Georgian has no uppercase (CLAUDE.md rule 3), so these are the letters as
 * written — `toUpperCase()` would be a no-op on Georgian and shout in English.
 */
function initialsOf(row: StaffRow, locale: Locale): string {
  const first = valueFor(row.first_name, locale).trim();
  const last = valueFor(row.last_name, locale).trim();

  return `${[...first][0] ?? ''}${[...last][0] ?? ''}` || '—';
}

function empty(facultyId: string): Fields {
  return {
    first_name: {},
    last_name: {},
    slug: {},
    position: {},
    rank: 'professor',
    faculty_id: facultyId,
    department_id: '',
    email: '',
    phone: '',
    photo_media_id: null,
    photo: null,
    bio: {},
    education: {},
    research_interests: {},
    publications: {},
    courses: {},
    orcid: '',
    scholar_url: '',
    researchgate_url: '',
    linkedin_url: '',
    is_academic: true,
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: StaffRow): Fields {
  return {
    first_name: row.first_name,
    last_name: row.last_name,
    slug: row.slug,
    position: row.position,
    rank: row.rank,
    faculty_id: row.faculty_id === null ? '' : String(row.faculty_id),
    department_id: row.department_id === null ? '' : String(row.department_id),
    email: row.email ?? '',
    phone: row.phone ?? '',
    photo_media_id: row.photo_media_id ?? null,
    photo: row.photo ?? null,
    bio: row.bio,
    education: row.education,
    research_interests: row.research_interests,
    publications: row.publications,
    courses: row.courses,
    orcid: row.orcid ?? '',
    scholar_url: row.scholar_url ?? '',
    researchgate_url: row.researchgate_url ?? '',
    linkedin_url: row.linkedin_url ?? '',
    is_academic: row.is_academic,
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): StaffInput {
  const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

  return {
    first_name: form.first_name,
    last_name: form.last_name,
    slug: form.slug,
    position: form.position,
    rank: form.rank,
    faculty_id: form.faculty_id === '' ? null : Number(form.faculty_id),
    department_id: form.department_id === '' ? null : Number(form.department_id),
    email: text(form.email),
    phone: text(form.phone),
    photo_media_id: form.photo_media_id,
    bio: form.bio,
    education: form.education,
    publications: form.publications,
    research_interests: listToInput(form.research_interests),
    courses: listToInput(form.courses),
    orcid: text(form.orcid),
    scholar_url: text(form.scholar_url),
    researchgate_url: text(form.researchgate_url),
    linkedin_url: text(form.linkedin_url),
    is_academic: form.is_academic,
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    // A Faculty Admin's save is a submission for review — §3 replaces
    // "publish" with "submit for review" in their UI.
    pending_review: mayPublish ? form.pending_review : true,
  };
}
